import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Header from "../components/Common/Header";
import {
  ConnectedPersonSummary,
  ConnectedPersonCreate,
  listConnectedPersons,
  createConnectedPerson,
  deleteConnectedPerson,
  getConnectedPersonDisplayName,
} from "../api/connectedPersons";

export default function ConnectedPersons() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<ConnectedPersonSummary | null>(null);

  // Form state for creating
  const [newPerson, setNewPerson] = useState<ConnectedPersonCreate>({
    given_name: "",
    surname: "",
    sex: "",
    birth_date_text: "",
    death_date_text: "",
    notes: "",
  });

  // Fetch connected persons
  const { data: persons, isLoading, error } = useQuery({
    queryKey: ["connectedPersons", search],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listConnectedPersons(token, search || undefined, 100);
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ConnectedPersonCreate) => {
      const token = await getAccessTokenSilently();
      return createConnectedPerson(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedPersons"] });
      setShowCreateModal(false);
      setNewPerson({
        given_name: "",
        surname: "",
        sex: "",
        birth_date_text: "",
        death_date_text: "",
        notes: "",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = await getAccessTokenSilently();
      return deleteConnectedPerson(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedPersons"] });
      setDeleteConfirm(null);
    },
  });

  const handleCreate = () => {
    if (!newPerson.given_name && !newPerson.surname) return;
    createMutation.mutate(newPerson);
  };

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <Link to="/dashboard" className="text-sm text-vault-600 hover:text-vault-900">
            ← Back to Dashboard
          </Link>
        </nav>

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-vault-900">
              Connected People
            </h1>
            <p className="mt-1 text-vault-600">
              People who are connected to your family but not in your family tree
              (friends, neighbors, colleagues, etc.)
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700"
          >
            + Add Person
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search connected people..."
            className="w-full max-w-md rounded-lg border border-vault-200 px-4 py-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-vault-300 border-t-vault-600" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-red-700">
            Failed to load connected people. Please try again.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && persons && persons.length === 0 && (
          <div className="rounded-lg bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center text-2xl">
              ⚭
            </div>
            <h3 className="text-lg font-medium text-vault-900">No connected people yet</h3>
            <p className="mt-1 text-vault-600">
              Add people who are connected to your family but not in your tree.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700"
            >
              Add Your First Connected Person
            </button>
          </div>
        )}

        {/* List */}
        {persons && persons.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {persons.map((person) => (
              <div
                key={person.id}
                className="rounded-lg bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <Link to={`/connected-persons/${person.id}`} className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-lg ${
                        person.sex === "M" 
                          ? "bg-blue-100 text-blue-700"
                          : person.sex === "F"
                          ? "bg-pink-100 text-pink-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {person.sex === "M" ? "♂" : person.sex === "F" ? "♀" : "⚭"}
                      </div>
                      <div>
                        <h3 className="font-medium text-vault-900">
                          {getConnectedPersonDisplayName(person)}
                        </h3>
                        <p className="text-sm text-vault-500">
                          {person.birth_date_text && `b. ${person.birth_date_text}`}
                          {person.birth_date_text && person.death_date_text && " · "}
                          {person.death_date_text && `d. ${person.death_date_text}`}
                        </p>
                      </div>
                    </div>
                  </Link>
                  <button
                    onClick={() => setDeleteConfirm(person)}
                    className="p-1 text-vault-400 hover:text-red-600"
                    title="Delete"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-xl font-semibold text-vault-900">
              Add Connected Person
            </h2>
            <p className="mb-4 text-sm text-vault-600">
              Add someone connected to your family who is not in your tree.
            </p>

            {createMutation.error && (
              <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">
                {createMutation.error instanceof Error ? createMutation.error.message : "Failed to create"}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newPerson.given_name}
                    onChange={(e) => setNewPerson({ ...newPerson, given_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newPerson.surname}
                    onChange={(e) => setNewPerson({ ...newPerson, surname: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Sex
                </label>
                <select
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={newPerson.sex}
                  onChange={(e) => setNewPerson({ ...newPerson, sex: e.target.value })}
                >
                  <option value="">Unknown</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    Birth Date
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., c. 1920"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newPerson.birth_date_text}
                    onChange={(e) => setNewPerson({ ...newPerson, birth_date_text: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    Death Date
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 1985"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newPerson.death_date_text}
                    onChange={(e) => setNewPerson({ ...newPerson, death_date_text: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Notes
                </label>
                <textarea
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  rows={3}
                  placeholder="How is this person connected to your family?"
                  value={newPerson.notes}
                  onChange={(e) => setNewPerson({ ...newPerson, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || (!newPerson.given_name && !newPerson.surname)}
                className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700 disabled:opacity-50"
              >
                {createMutation.isPending ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-vault-900">
              Delete Connected Person?
            </h2>
            <p className="mb-4 text-vault-600">
              Are you sure you want to delete{" "}
              <span className="font-medium">{getConnectedPersonDisplayName(deleteConfirm)}</span>?
              This will also remove all their connections and photo tags.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
